import {
  Box,
  Container,
  Text,
  VStack,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CaseStudiesGrid from "../Common/CaseStudiesGrid";
import SeeMoreButton from "../Common/SeeMoreButton";
import { caseStudiesService } from "../../services/caseStudiesService";
import { getStaticCaseStudyDescription, isStaticCaseStudy } from "../../utils/caseStudyData";

export default function CaseStudiesSection() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        setLoading(true);
        const response = await caseStudiesService.getAllCaseStudies();
        if (response.success && response.data) {
          // Transform API data to match component structure
          const transformedData = response.data.slice(0, 3).map((study, index) => {
            
            // Get static description if available, otherwise use API description
            const staticDescription = getStaticCaseStudyDescription(study.imageHeader.title);
            
            return {
              id: study.id,
              documentId: study.documentId,
              slug: study.slug,
              image: study.cardImage?.url 
                ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${study.cardImage.url}`
                : study.featuredImage?.[0]?.url 
                  ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${study.featuredImage[0].url}`
                  : `/clients/cs0${index + 1}.png`, // Fallback to existing images
              title: study.imageHeader.title,
              description: staticDescription || study.projectInfo?.description || study.imageHeader?.description,
              // Enhanced keyResults transformation with better fallback
              results: study.keyResults && study.keyResults.length > 0 
                ? study.keyResults.slice(0, 4).map(result => ({
                    title: result.description || result.metric || result.title || 'Key Result',
                    description: result.details || '',
                  }))
                : study.Results && study.Results.length > 0
                  ? study.Results.slice(0, 4).map(result => ({
                      title: result.title || 'Achievement',
                      description: result.description || '',
                    }))
                  : [],
              category: study.categories?.[0]?.name || study.category || study.serviceType || "Digital Solutions",
              categoryColor: "blue",
            };
          });
          setCaseStudies(transformedData);
        } else {
          setError("Failed to load case studies");
        }
      } catch (err) {
        console.error("Error fetching case studies:", err);
        setError("Failed to load case studies");
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, []);

  if (loading) {
    return (
      <Box  py={20} minH="100vh">
        <Container maxW="7xl" px={8}>
          <Flex justify="center" align="center" minH="400px">
            <Spinner size="xl" color="#000B8C" />
          </Flex>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box  py={20} minH="100vh">
        <Container maxW="7xl" px={8}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={20} minH="100vh">
      <Container maxW="7xl" px={8}>
        {/* Section Header */}
        <VStack spacing={6} textAlign="center" mb={16}>
          <Text
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            fontWeight="bold"
            lineHeight="shorter"
          >
            <Text as="span" color="#000B8C">
              Case
            </Text>{" "}
            <Text as="span" color="gray.800">
              Studies
            </Text>
          </Text>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.600"
            maxW="3xl"
            lineHeight="relaxed"
          >
            Discover how our innovative solutions have transformed organizations and
            delivered measurable results across various industries.
          </Text>
        </VStack>

        <Flex wrap="wrap" gap={6} mb={10} justify="center" alignItems="stretch">
          {caseStudies.map((study) => (
            <Box
              key={study.id}
              maxW="360px"
              minW="260px"
              flex="1"
              display="flex"
              flexDirection="column"
            >
              <CaseStudiesGrid
                study={study}
                boxProps={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              />
            </Box>
          ))}
        </Flex>
        <SeeMoreButton
          onClick={() => navigate("/case-studies")}
          text="See More Case Studies"
          colors={{
            textColor: "black",
            textBorder: "1px solid black",
            textHover: "#000B8C",
            iconBg: "rgba(0, 11, 140, 1)",
            iconHover: "#030b3aff",
          }}
        />
      </Container>
    </Box>
  );
}
